import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate, Link } from 'react-router';
import { ArrowRight, Brain, MessageCircle, Zap, Eye, Activity, TrendingUp } from 'lucide-react';

import { AmbientBackground, SectionGlow } from './background/AmbientBackground';
import { MainNavigation } from './navigation/MainNavigation';
import { HeroSection } from './home/HeroSection';
import { HeroFeatureStrip } from './home/HeroFeatureStrip';

/* ─── Shared scroll-reveal helper ─── */
function useReveal(rm: boolean | null) {
  return (delay = 0, y = 26) => ({
    initial:     { opacity: 0, y: rm ? 0 : y },
    whileInView: { opacity: 1, y: 0 },
    viewport:    { once: true, margin: '-64px' } as const,
    transition:  { duration: rm ? 0 : 0.76, delay, ease: [0.22, 1, 0.36, 1] as const },
  });
}

/* ─── Section wrapper — atmospheric carry-over, no hard dividers ─── */
function Section({
  id,
  children,
  className = '',
}: {
  id?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <>
      {/* Atmospheric breath zone — lets fixed background show through */}
      <div
        className="pointer-events-none"
        style={{
          height: 1,
          background: 'transparent',
        }}
      />
      <section
        id={id}
        className={`t-section relative mx-auto ${className}`}
        style={{ maxWidth: 'min(1100px, 92vw)' }}
      >
        {children}
      </section>
    </>
  );
}

/* ─── How It Works card ─── */
function HowCard({
  step, icon: Icon, title, body, delay, accent,
}: {
  step: string;
  icon: typeof Brain;
  title: string;
  body: string;
  delay: number;
  accent: string;
}) {
  const rm = useReducedMotion();
  const reveal = useReveal(rm);
  return (
    <motion.div
      className="t-card group relative flex flex-col gap-5 overflow-hidden"
      {...reveal(delay)}
      whileHover={rm ? undefined : { y: -3, transition: { duration: 0.28, ease: [0.22,1,.36,1] } }}
    >
      {/* Accent top border */}
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${accent}55, transparent)`,
          transition: 'opacity .3s',
        }}
      />

      <div className="flex items-center gap-4">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[12px] font-bold"
          style={{
            background: `${accent}18`,
            border: `1px solid ${accent}30`,
            color: accent,
          }}
        >
          {step}
        </span>
        <Icon className="h-5 w-5 stroke-[1.75]" style={{ color: accent }} />
      </div>

      <div>
        <h3 className="mb-2.5 text-[19px] font-semibold" style={{ color: '#F4F6FC' }}>{title}</h3>
        <p className="text-[15px] leading-[1.74]" style={{ color: 'rgba(244,246,252,.55)' }}>{body}</p>
      </div>

      {/* Hover bottom glow */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[1px] origin-center scale-x-0 rounded-full transition-transform duration-400 group-hover:scale-x-100"
        style={{ background: `linear-gradient(90deg, transparent, ${accent}55, transparent)` }}
      />
    </motion.div>
  );
}

/* ─── Metric chip ─── */
function MetricChip({ label, value, pct = 68 }: { label: string; value: string; pct?: number }) {
  return (
    <div
      className="rounded-[18px] p-4"
      style={{
        background: 'rgba(255,255,255,.035)',
        border: '1px solid rgba(255,255,255,.06)',
      }}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.20em]"
         style={{ color: 'rgba(244,246,252,.40)' }}>
        {label}
      </p>
      <p className="mt-2 text-[19px] font-bold" style={{ color: '#F4F6FC' }}>{value}</p>
      <div className="mt-3 h-[3px] overflow-hidden rounded-full" style={{ background: 'rgba(255,255,255,.07)' }}>
        <div
          className="h-full rounded-full"
          style={{
            width: `${pct}%`,
            background: 'linear-gradient(90deg, #E8179B, #9B4FE8, #1A5FE8)',
          }}
        />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN EXPORT
   ══════════════════════════════════════════════════════════ */
export default function TensionExperience() {
  const navigate   = useNavigate();
  const rm         = useReducedMotion();
  const reveal     = useReveal(rm);

  const goSignup  = () => navigate('/auth?signup=true');
  const goSignIn  = () => navigate('/auth');
  const scrollHow = () => document.querySelector('#how')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div
      className="tension-site relative min-h-screen overflow-x-hidden"
      style={{ backgroundColor: '#030712' }}
    >
      {/* Atmospheric background — fixed so it persists while scrolling */}
      <AmbientBackground variant="hero" className="fixed" />

      {/* Navigation */}
      <MainNavigation onGetStarted={goSignup} onSignIn={goSignIn} />

      {/* ─── Hero ─── */}
      <HeroSection onGetStarted={goSignup} onSeeHow={scrollHow} />

      {/* ─── Feature dock ─── */}
      <HeroFeatureStrip />

      {/* ════════════════════════════════════════
          SECTION: THE UNSEEN SIGNAL
          ════════════════════════════════════════ */}
      <Section id="about">
        <SectionGlow color="fuchsia" position="left" intensity="soft" />

        <div className="relative grid gap-12 md:grid-cols-2 md:gap-20">
          {/* Left: emotional copy */}
          <motion.div {...reveal(0)}>
            <p className="t-eyebrow mb-6">The Unseen Signal</p>
            <h2 className="t-headline mb-6" style={{ color: '#F4F6FC' }}>
              You've felt it.<br />
              <span className="t-gradient-warm">Before you could name it.</span>
            </h2>
            <div className="t-rule mb-8" />
            <div
              className="space-y-4 text-[17px] leading-[1.78]"
              style={{ color: 'rgba(244,246,252,.58)' }}
            >
              <p>Something feels off — but you can't explain it.</p>
              <p>Conversations shift in ways you can't quite track.</p>
              <p>Distance builds, silently, before a word is raised.</p>
            </div>
            <p className="mt-9 text-[20px] font-semibold" style={{ color: '#F4F6FC' }}>
              That's exactly what TENSION detects.
            </p>
          </motion.div>

          {/* Right: live intelligence card */}
          <motion.div
            className="t-card flex flex-col gap-5"
            {...reveal(0.14)}
          >
            <div className="flex items-center gap-2.5">
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: '#4AE88C' }}
              />
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em]"
                 style={{ color: 'rgba(244,246,252,.36)' }}>
                Live Intelligence
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <MetricChip label="Tension Score"   value="72 → Rising" pct={72} />
              <MetricChip label="Pattern Signal"  value="3-day shift"  pct={58} />
              <MetricChip label="Risk Level"      value="Moderate ↑"  pct={62} />
            </div>

            <div
              className="rounded-[18px] p-4"
              style={{
                background: 'rgba(3,7,18,.60)',
                border: '1px solid rgba(255,255,255,.055)',
              }}
            >
              <div className="mb-3 flex items-center gap-2">
                <Eye className="h-3.5 w-3.5 stroke-[1.75]" style={{ color: '#9B4FE8' }} />
                <p className="text-[11.5px] font-semibold"
                   style={{ color: 'rgba(244,246,252,.52)' }}>
                  5-Dimension Relationship Radar
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {['Communication','Emotional Alignment','Responsiveness','Consistency','Conflict Signals'].map(d => (
                  <span
                    key={d}
                    className="rounded-[10px] px-3 py-1.5 text-[11.5px] font-medium"
                    style={{
                      background: 'rgba(255,255,255,.04)',
                      border: '1px solid rgba(255,255,255,.07)',
                      color: 'rgba(244,246,252,.68)',
                    }}
                  >
                    {d}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ════════════════════════════════════════
          SECTION: HOW IT WORKS
          ════════════════════════════════════════ */}
      <Section id="how">
        {/* Subtle signal wave background within section */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage: 'url(/assets/tension/backgrounds/signal-wave-section-bg.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'screen',
          }}
        />
        <SectionGlow color="violet" position="center" intensity="soft" />

        <div className="relative">
          <motion.div {...reveal(0)} className="mb-14">
            <p className="t-eyebrow mb-5">How It Works</p>
            <h2 className="t-headline" style={{ maxWidth: 620, color: '#F4F6FC' }}>
              Intelligence that moves at<br />
              <span className="t-gradient-text">the speed of emotion.</span>
            </h2>
          </motion.div>

          <div className="grid gap-4 md:grid-cols-3">
            <HowCard
              step="01"
              icon={MessageCircle}
              title="Log Real Moments"
              body="Capture real interactions — not assumptions. The system learns from what actually happens between you."
              delay={0.04}
              accent="#E8179B"
            />
            <HowCard
              step="02"
              icon={Brain}
              title="Detect Hidden Patterns"
              body="AI reads emotional shifts, tone drift, and behavioral signals that humans naturally miss in the moment."
              delay={0.11}
              accent="#9B4FE8"
            />
            <HowCard
              step="03"
              icon={Zap}
              title="Predict What's Coming"
              body="Know where a relationship is heading before it arrives. Act with clarity and emotional intelligence."
              delay={0.18}
              accent="#00A8F0"
            />
          </div>

          <motion.p
            className="mt-10 text-[16px]"
            style={{ color: 'rgba(244,246,252,.48)' }}
            {...reveal(0.26)}
          >
            Like a weather forecast — but for your most important relationships.
          </motion.p>
        </div>
      </Section>

      {/* ════════════════════════════════════════
          SECTION: NOT THERAPY
          ════════════════════════════════════════ */}
      <Section>
        <SectionGlow color="blue" position="right" intensity="soft" />

        <div className="relative grid items-center gap-12 md:grid-cols-2 md:gap-20">
          {/* Dashboard preview */}
          <motion.div {...reveal(0)}>
            <div
              className="overflow-hidden rounded-[22px]"
              style={{
                boxShadow: [
                  '0 1px 0 rgba(255,255,255,.07) inset',
                  '0 40px 100px rgba(0,0,0,.64)',
                  '0 0 0 1px rgba(255,255,255,.05)',
                ].join(', '),
              }}
            >
              <img
                src="/assets/tension/hero/homepage-relationship-dashboard-preview.png"
                alt="TENSION relationship dashboard"
                className="h-auto w-full"
                style={{ display: 'block' }}
                loading="lazy"
                decoding="async"
              />
            </div>
          </motion.div>

          <motion.div {...reveal(0.14)}>
            <p className="t-eyebrow mb-6">A New Intelligence Layer</p>
            <h2 className="t-headline mb-6" style={{ color: '#F4F6FC' }}>
              This isn't therapy.<br />
              <span className="t-gradient-warm">This isn't advice.</span>
            </h2>
            <div className="t-rule mb-8" />
            <p
              className="mb-5 text-[17px] leading-[1.76]"
              style={{ color: 'rgba(244,246,252,.58)' }}
            >
              This is a new layer of intelligence — a system that makes invisible
              relationship patterns visible, measurable, and actionable.
            </p>
            <p
              className="text-[15.5px] leading-[1.74]"
              style={{ color: 'rgba(244,246,252,.40)' }}
            >
              TENSION is the first Relationship Intelligence System: it detects, measures,
              and predicts emotional shifts before they become lasting damage.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* ════════════════════════════════════════
          SECTION: TENSION SCORE
          ════════════════════════════════════════ */}
      <Section>
        {/* Pricing bg layer */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.16]"
          style={{
            backgroundImage: 'url(/assets/tension/backgrounds/pricing-page-bg.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'screen',
          }}
        />
        <SectionGlow color="cyan" position="center" intensity="soft" />

        <motion.div
          className="relative mx-auto text-center"
          style={{ maxWidth: 820 }}
          {...reveal(0)}
        >
          <p className="t-eyebrow mb-7">Your Score</p>
          <h2 className="t-headline mb-6" style={{ color: '#F4F6FC' }}>
            Your Relationship Tension Score.<br />
            <span className="t-gradient-text">Finally, a number that means something.</span>
          </h2>
          <p
            className="t-body-lg mx-auto mb-12"
            style={{ maxWidth: 580 }}
          >
            People will soon measure relationship tension the way they measure steps,
            sleep, and heart rate. Be among the first.
          </p>

          <motion.div
            className="t-card mx-auto grid grid-cols-3 gap-4"
            style={{ maxWidth: 660 }}
            {...reveal(0.10)}
          >
            <MetricChip label="Score"   value="72"      pct={72} />
            <MetricChip label="Status"  value="Rising"  pct={82} />
            <MetricChip label="Trend"   value="↑ +8%"   pct={60} />
          </motion.div>

          <motion.button
            type="button"
            onClick={goSignup}
            className="t-btn-primary mt-12 inline-flex items-center gap-2.5"
            style={{ paddingInline: 44, paddingBlock: 18, borderRadius: 18, fontSize: 16 }}
            whileHover={rm ? undefined : { y: -2, scale: 1.03 }}
            whileTap={rm ? undefined : { scale: 0.975 }}
            transition={{ type: 'spring', stiffness: 380, damping: 26 }}
            {...reveal(0.18)}
          >
            Start Your Journey
            <ArrowRight className="h-4 w-4 stroke-[2.5]" />
          </motion.button>
        </motion.div>
      </Section>

      {/* ════════════════════════════════════════
          SECTION: FINAL CTA
          ════════════════════════════════════════ */}
      <Section>
        {/* Footer wave glow — atmospheric base for this section */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.20]"
          style={{
            backgroundImage: 'url(/assets/tension/backgrounds/footer-wave-glow.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center bottom',
            mixBlendMode: 'screen',
          }}
        />
        <SectionGlow color="violet" position="center" intensity="soft" />

        <motion.div
          className="relative mx-auto text-center"
          style={{ maxWidth: 700 }}
          {...reveal(0)}
        >
          <p className="t-eyebrow mb-7">Early Access</p>
          <h2 className="t-headline mb-6" style={{ color: '#F4F6FC' }}>
            Act before it costs you<br />
            <span className="t-gradient-warm">a relationship that matters.</span>
          </h2>
          <p
            className="t-body-lg mx-auto mb-10"
            style={{ maxWidth: 520 }}
          >
            Private beta access is rolling out in waves.
            Every day without clarity is a day of silent drift.
          </p>

          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <motion.button
              type="button"
              onClick={goSignup}
              className="t-btn-primary inline-flex items-center gap-2.5"
              style={{ paddingInline: 40, paddingBlock: 17, borderRadius: 18, fontSize: 15 }}
              whileHover={rm ? undefined : { y: -2, scale: 1.03 }}
              whileTap={rm ? undefined : { scale: 0.975 }}
              transition={{ type: 'spring', stiffness: 380, damping: 26 }}
            >
              Start Your Journey
              <ArrowRight className="h-4 w-4 stroke-[2.5]" />
            </motion.button>
            <button
              type="button"
              onClick={goSignIn}
              className="t-btn-secondary"
              style={{ paddingInline: 36, paddingBlock: 16, borderRadius: 18, fontSize: 15 }}
            >
              Sign In
            </button>
          </div>
          <p
            className="mt-6 text-[12px] font-medium tracking-[0.10em]"
            style={{ color: 'rgba(244,246,252,.28)' }}
          >
            Early users help shape the future of TENSION.
          </p>
        </motion.div>
      </Section>

      {/* ════════════════════════════════════════
          FOOTER
          ════════════════════════════════════════ */}
      <footer
        className="relative overflow-hidden px-6"
        style={{
          background: 'rgba(3,7,18,.97)',
          borderTop: '1px solid rgba(255,255,255,.048)',
          paddingBlock: 'clamp(44px, 6vw, 72px)',
        }}
      >
        {/* Ambient violet carry-over from last section */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0"
          style={{
            height: 180,
            background: 'radial-gradient(ellipse 60% 100% at 50% 0%, rgba(155,79,232,.07), transparent 100%)',
          }}
        />

        <div
          className="relative mx-auto grid gap-10 md:grid-cols-[auto_1fr_auto] md:items-start"
          style={{ maxWidth: 'min(1100px, 92vw)' }}
        >
          {/* Left — logo + tagline */}
          <div className="flex flex-col gap-3">
            <Link to="/" aria-label="TENSION home">
              <img
                src="/assets/tension/brand/logo-main.png"
                alt="TENSION"
                className="h-auto"
                style={{ width: 128 }}
                loading="lazy"
                decoding="async"
              />
            </Link>
            <p
              className="max-w-[200px] text-[12.5px] leading-relaxed"
              style={{ color: 'rgba(244,246,252,.36)' }}
            >
              The future of understanding the people who matter most.
            </p>
          </div>

          {/* Center — navigation */}
          <nav className="flex flex-wrap gap-x-8 gap-y-3 md:justify-center md:pt-1">
            {[
              { label: 'Product',      href: '#product'  },
              { label: 'Features',     href: '#features' },
              { label: 'How It Works', href: '#how'      },
              { label: 'Pricing',      href: '/pricing'  },
              { label: 'About',        href: '#about'    },
            ].map(({ label, href }) => (
              href.startsWith('#') ? (
                <a
                  key={label}
                  href={href}
                  className="text-[13px] font-medium transition-colors duration-200 hover:text-white/80"
                  style={{ color: 'rgba(244,246,252,.42)' }}
                >
                  {label}
                </a>
              ) : (
                <Link
                  key={label}
                  to={href}
                  className="text-[13px] font-medium transition-colors duration-200 hover:text-white/80"
                  style={{ color: 'rgba(244,246,252,.42)' }}
                >
                  {label}
                </Link>
              )
            ))}
          </nav>

          {/* Right — CTA + copyright */}
          <div className="flex flex-col items-start gap-4 md:items-end">
            <button
              type="button"
              onClick={() => window.location.href = '/auth?signup=true'}
              className="t-btn-primary text-[13px]"
              style={{ paddingInline: 24, paddingBlock: 10, borderRadius: 13, fontSize: 13, fontWeight: 600 }}
            >
              Start Your Journey
            </button>
            <p className="text-[12px]" style={{ color: 'rgba(244,246,252,.22)' }}>
              © {new Date().getFullYear()} Tension Labs
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
